import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { CorLiturgica, OracaoHoje, SituacaoOracao } from '@/lib/oracao';
import {
  MOSTRAR_QUANTOS_REZARAM,
  dataLocalDe,
  dataLongaPt,
  textoDesafios,
  textoDias,
  textoPessoas,
} from '@/lib/oracao';
import { parseEvangelho } from '../biblia';
import { diasDaSemana } from '../semana';
import type { Passo } from '../types';
import {
  DICA_BIBLIA,
  DICA_BIBLIA_2,
  FIM_AMANHA,
  LINK_CNBB,
  PAI_NOSSO,
  PRESENCA_INTRO,
  PRESENCA_LINHA_1,
  PRESENCA_LINHA_2,
  PRESENCA_ORACAO_FIM,
  PRESENCA_ORACAO_INICIO,
  ROMANOS,
  RUBRICA_PADRE,
  RUBRICA_VIDA,
  SINAL_CRUZ,
} from '../constantes';
import ContadorSilencio from './ContadorSilencio';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import PrateleiraBiblia from './PrateleiraBiblia';
import PlayerPadre from './PlayerPadre';
import Respiro from './Respiro';
import ArteSemImagem from './ArteSemImagem';

interface Props {
  passo: Passo;
  oracao: OracaoHoje;
  situacao: SituacaoOracao | null;
  cor: CorLiturgica;
  previa: boolean;
  desafioAceito: boolean;
  desafiosHoje: number;
  ativandoAvisos: boolean;
  mostrarAtivarAvisos: boolean;
  perguntaAtual: number;
  perguntas: string[];
  aoCompartilhar: () => void;
  aoAtivarAvisos: () => void;
  aoInstalar: () => void;
}

function emAppInstalado(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

// Conteúdo de cada passo (os botões Continuar/Voltar ficam no rodapé da página).
export default function ConteudoPasso({
  passo,
  oracao,
  situacao,
  cor,
  previa,
  desafioAceito,
  desafiosHoje,
  ativandoAvisos,
  mostrarAtivarAvisos,
  perguntaAtual,
  perguntas,
  aoCompartilhar,
  aoAtivarAvisos,
  aoInstalar,
}: Props) {
  const diaData = oracao.dia ? dataLocalDe(oracao.dia) : null;
  const leitura = parseEvangelho(oracao.evangelho_ref);

  // Faíscas/selo do desafio: só animam quando a pessoa aceita AGORA.
  const aceitoAntes = useRef(desafioAceito);
  const [faiscas, setFaiscas] = useState(false);
  const [seloAnimado, setSeloAnimado] = useState(false);
  useEffect(() => {
    if (desafioAceito && !aceitoAntes.current) {
      setFaiscas(true);
      setSeloAnimado(true);
      const t = window.setTimeout(() => setFaiscas(false), 900);
      aceitoAntes.current = true;
      return () => window.clearTimeout(t);
    }
    aceitoAntes.current = desafioAceito;
    return undefined;
  }, [desafioAceito]);

  const semana = situacao?.semana ?? [false, false, false, false, false, false, false];
  const dias = diasDaSemana();

  return (
    <div className="passo-entra" key={passo.id}>
      {passo.id !== 'capa' && passo.id !== 'final' && (passo.rotulo || passo.titulo) && (
        <div className="pr-11">
          {passo.rotulo && <p className="rotulo">{passo.rotulo}</p>}
          {passo.titulo && <h2 className="titulo-passo mt-2">{passo.titulo}</h2>}
        </div>
      )}

      {/* capa */}
      {passo.id === 'capa' && (
        <div className="capa-texto flex min-h-[calc(100dvh-11rem)] flex-col pt-2">
          <div>
            {diaData && (
              <p className="text-[14px] text-[color:var(--texto-2)]">{dataLongaPt(diaData)}</p>
            )}
            {oracao.tempo_liturgico && (
              <p className="mt-1 text-[13px] text-[color:var(--texto-2)]">{oracao.tempo_liturgico}</p>
            )}
            {oracao.cor_liturgica && (
              <p className="mt-1 flex items-center gap-2 text-[13px] text-[color:var(--texto-2)]">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: cor.fita }}
                  aria-hidden
                />
                Cor litúrgica: {oracao.cor_liturgica}
              </p>
            )}
          </div>

          {!oracao.imagem_url && (
            <div className="flex flex-1 items-center justify-center py-4">
              <ArteSemImagem
                style={{ height: 'clamp(140px, 30vh, 300px)', width: 'auto' }}
              />
            </div>
          )}
          {oracao.imagem_url && <div className="flex-1" />}

          <div className="pb-3">
            {oracao.santo && (
              <p
                className="leitura italic"
                style={{ color: 'var(--ouro-forte)', fontSize: 24 }}
              >
                {oracao.santo}
              </p>
            )}
            {oracao.santo_titulo && (
              <p className="mt-1 text-[13px] text-[color:var(--texto-2)]">
                {oracao.santo_titulo}
              </p>
            )}

            {oracao.tema && (
              <h1 className="leitura mt-4" style={{ fontSize: 40, lineHeight: 1.08 }}>
                {oracao.tema}
              </h1>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {oracao.evangelho_ref && (
                <span className="botao-contorno inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px]">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-book-open-line" aria-hidden />
                  </span>
                  {oracao.evangelho_ref}
                </span>
              )}
              <span className="text-[13px] text-[color:var(--texto-2)]">Cerca de 10 minutos</span>
            </div>

            {oracao.video_url && (
              <p className="mt-3 text-[14px] text-[color:var(--ouro-forte)]">
                ▶ {oracao.video_autor ?? 'Um padre'} deixa uma palavra para hoje
              </p>
            )}

            {situacao?.rezei_hoje && (
              <div className="mt-5">
                <div className="flex items-center gap-[10px]">
                  {semana.map((rezou, i) => (
                    <span
                      key={i}
                      className="h-3.5 w-3.5 rounded-full"
                      style={{
                        backgroundColor: rezou ? 'var(--ouro)' : 'transparent',
                        border: rezou ? 'none' : '1px solid rgba(217,178,95,.5)',
                      }}
                      aria-hidden
                    />
                  ))}
                </div>
                <p className="mt-2 text-[14px] text-[color:var(--ouro-forte)]">
                  Você rezou hoje · {textoDias(situacao.dias_na_semana)}
                </p>
              </div>
            )}

            {oracao.imagem_credito && (
              <p className="mt-4 text-[11px] leading-snug text-[color:var(--texto-2)] opacity-70">
                Imagem: {oracao.imagem_credito}
              </p>
            )}
          </div>
        </div>
      )}

      {/* presenca */}
      {passo.id === 'presenca' && (
        <div className="mt-2">
          <Respiro />
          <p className="rubrica mt-5">{PRESENCA_INTRO}</p>
          <div className="oracao-texto mt-4 space-y-2.5">
            <p className="linha-entra">{PRESENCA_ORACAO_INICIO}</p>
            <p className="linha-entra" style={{ animationDelay: '90ms' }}>
              {PRESENCA_LINHA_1}
            </p>
            <p className="linha-entra" style={{ animationDelay: '180ms' }}>
              <strong>{PRESENCA_LINHA_2}</strong>
            </p>
            <p className="linha-entra" style={{ animationDelay: '270ms' }}>
              {PRESENCA_ORACAO_FIM}
            </p>
          </div>
        </div>
      )}

      {/* santo */}
      {passo.id === 'santo' && (
        <div className="mt-6">
          {oracao.santo_titulo && (
            <p className="mb-4 text-[13px] text-[color:var(--texto-2)]">{oracao.santo_titulo}</p>
          )}

          {oracao.imagem_url ? (
            <figure className="mx-auto max-w-[300px]">
              <div className="moldura-arco">
                <img src={oracao.imagem_url} alt={oracao.santo ?? 'Santo do dia'} />
              </div>
              {oracao.imagem_credito && (
                <figcaption className="mt-2 text-center text-[11px] text-[color:var(--texto-2)] opacity-70">
                  {oracao.imagem_credito}
                </figcaption>
              )}
            </figure>
          ) : (
            <ArteSemImagem className="mx-auto h-[180px] w-auto" />
          )}

          {oracao.santo_texto && (
            <p className="leitura capitular mt-4 whitespace-pre-line">{oracao.santo_texto}</p>
          )}
        </div>
      )}

      {/* biblia */}
      {passo.id === 'biblia' && (
        <div className="mt-6">
          {leitura ? (
            <PrateleiraBiblia leitura={leitura} />
          ) : (
            <ArteSemImagem className="mx-auto h-[180px] w-auto" />
          )}

          <p className="leitura mt-6">
            {oracao.leitura_guia
              ? oracao.leitura_guia
              : `Abra a sua Bíblia em ${oracao.evangelho_ref ?? 'o Evangelho de hoje'}.`}
          </p>

          <div className="mt-5 flex items-start gap-4 rounded-[14px] border border-[rgba(217,178,95,.25)] p-4">
            <div className="flex items-end gap-1">
              <span className="leitura" style={{ fontSize: 44, lineHeight: 1 }}>
                11
                <span className="align-super text-[14px]">42</span>
              </span>
            </div>
            <div className="mt-2 flex flex-1 flex-col gap-1.5">
              <span className="h-1.5 w-full max-w-[140px] rounded-full bg-white/15" />
              <span className="h-1.5 w-full max-w-[110px] rounded-full bg-white/15" />
              <span className="h-1.5 w-full max-w-[90px] rounded-full bg-white/15" />
            </div>
          </div>
          <p className="mt-3 text-[14px] text-[color:var(--texto-2)]">{DICA_BIBLIA}</p>
          <p className="rubrica mt-3">{DICA_BIBLIA_2}</p>

          <a
            href={LINK_CNBB}
            target="_blank"
            rel="noopener noreferrer"
            className="aperta mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[14px] font-semibold text-[color:var(--ouro-forte)]"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-external-link-line" aria-hidden />
            </span>
            Está sem a Bíblia agora? Leia no site da CNBB
          </a>
        </div>
      )}

      {/* entenda */}
      {passo.id === 'entenda' && oracao.resumo && (
        <p className="leitura capitular mt-6">{oracao.resumo}</p>
      )}

      {/* padre */}
      {passo.id === 'padre' && oracao.video_url && (
        <div className="mt-6">
          <p className="leitura italic" style={{ color: 'var(--ouro-forte)', fontSize: 20 }}>
            {oracao.video_autor ?? 'Um padre'}
          </p>
          <div className="mt-4">
            <PlayerPadre url={oracao.video_url} />
          </div>
          <p className="rubrica mt-4">{RUBRICA_PADRE}</p>
        </div>
      )}

      {/* medite */}
      {passo.id === 'medite' && oracao.reflexao && (
        <div className="mt-6">
          {oracao.reflexao
            .split(/\n+/)
            .map((p) => p.trim())
            .filter(Boolean)
            .map((par, i) => (
              <p key={i} className={`leitura ${i === 0 ? 'capitular' : ''}`} style={{ marginTop: i === 0 ? 0 : '1em' }}>
                {par}
              </p>
            ))}
        </div>
      )}

      {/* vida */}
      {passo.id === 'vida' && perguntas.length > 0 && (
        <div className="mt-6">
          <p className="rubrica">{RUBRICA_VIDA}</p>
          <div className="mt-8 text-center">
            <p className="leitura" style={{ color: 'var(--ouro)', fontSize: 30, lineHeight: 1 }}>
              {ROMANOS[perguntaAtual] ?? perguntaAtual + 1}
            </p>
            <p
              className="leitura italic mt-3"
              style={{ fontSize: 'calc(26px * var(--escala, 1))', lineHeight: 1.35 }}
            >
              {perguntas[perguntaAtual]}
            </p>
            <p className="mt-4 text-[13px] text-[color:var(--texto-2)]">
              {perguntaAtual + 1} de {perguntas.length}
            </p>
          </div>
        </div>
      )}

      {/* reze */}
      {passo.id === 'reze' && oracao.texto && (
        <div className="oracao-texto mt-6">
          {oracao.texto
            .split(/\n+/)
            .map((linha) => linha.trim())
            .filter(Boolean)
            .map((linha, i) => (
              <p key={i} className="linha-entra versiculo mt-2" style={{ animationDelay: `${i * 90}ms` }}>
                {linha}
              </p>
            ))}
        </div>
      )}

      {/* contemple */}
      {passo.id === 'contemple' && (
        <div className="mt-6">
          {oracao.silencio && <p className="rubrica">{oracao.silencio}</p>}
          <div className="mt-8">
            <ContadorSilencio />
          </div>
        </div>
      )}

      {/* viva */}
      {passo.id === 'viva' && (
        <div className="mt-6">
          {oracao.gesto && (
            <div className="santinho">
              <svg
                viewBox="0 0 24 24"
                className="absolute left-1/2 top-3 h-6 w-6 -translate-x-1/2"
                aria-hidden
              >
                <line x1="12" y1="3" x2="12" y2="21" stroke="var(--ouro)" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="6" y1="9" x2="18" y2="9" stroke="var(--ouro)" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
              <p
                className="leitura mt-4 text-center"
                style={{ fontSize: 22, lineHeight: 1.45 }}
              >
                {oracao.gesto}
              </p>

              {faiscas &&
                Array.from({ length: 12 }).map((_, i) => (
                  <span
                    key={i}
                    className="faisca"
                    style={{ '--a': `${i * 30}deg` } as CSSProperties}
                    aria-hidden
                  />
                ))}

              {desafioAceito && (
                <div
                  className="selo"
                  style={seloAnimado ? undefined : { animation: 'none' }}
                  aria-hidden
                >
                  <span>✓</span>
                  <span>Aceito</span>
                </div>
              )}
            </div>
          )}

          {desafioAceito && (
            <div className="mt-5 text-center">
              <p className="text-[16px] font-semibold text-[color:var(--ouro-forte)]">
                Desafio aceito
              </p>
              <p className="mt-1 text-[14px] text-[color:var(--texto-2)]">
                {textoDesafios(desafiosHoje)}
              </p>
            </div>
          )}

          {previa && !desafioAceito && (
            <p className="mt-4 text-center text-[12px] text-[color:var(--texto-2)]">
              Numa prévia o desafio fica desativado.
            </p>
          )}
        </div>
      )}

      {/* termine */}
      {passo.id === 'termine' && (
        <div className="mt-6">
          <div className="oracao-texto">
            {PAI_NOSSO.map((linha, i) => (
              <p key={linha} className="linha-entra versiculo mt-2" style={{ animationDelay: `${i * 90}ms` }}>
                {linha}
              </p>
            ))}
          </div>
          {oracao.santo_pedido && (
            <p className="leitura italic mt-5" style={{ color: 'var(--ouro-forte)' }}>
              {oracao.santo_pedido}
            </p>
          )}
          <p className="leitura mt-5">{SINAL_CRUZ}</p>
          {previa && (
            <p className="mt-3 text-[12px] text-[color:var(--texto-2)]">
              Numa prévia o botão "Rezei" fica desativado.
            </p>
          )}
        </div>
      )}

      {/* final */}
      {passo.id === 'final' && (
        <div className="pt-6 text-center">
          <CheckAnimado
            tamanho={96}
            cor="#E8C877"
            fundo="rgba(232,200,119,.14)"
            className="mx-auto"
          />

          <p className="leitura mt-4" style={{ fontSize: 34, lineHeight: 1.1 }}>
            Oração feita hoje
          </p>

          <div className="mt-5 flex justify-center gap-2.5">
            {dias.map((d, i) => {
              const rezou = semana[i] === true;
              return (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <span
                    className="h-3.5 w-3.5 rounded-full"
                    style={{
                      backgroundColor: rezou ? 'var(--ouro)' : 'transparent',
                      border: rezou ? 'none' : '1px solid rgba(217,178,95,.5)',
                      boxShadow: d.hoje ? '0 0 0 3px rgba(217,178,95,.28)' : undefined,
                    }}
                    aria-hidden
                  />
                  <span className="text-[12px] text-[color:var(--texto-2)]">
                    {d.hoje ? 'hoje' : d.letra}
                  </span>
                </div>
              );
            })}
          </div>

          {situacao && (
            <div className="mt-4">
              <p className="text-[15px] text-[color:var(--texto)]">
                {textoDias(situacao.dias_na_semana)}
              </p>
              {MOSTRAR_QUANTOS_REZARAM && (
                <p className="mt-0.5 text-[15px] text-[color:var(--texto-2)]">
                  {textoPessoas(situacao.pessoas_hoje)}
                </p>
              )}
            </div>
          )}

          {oracao.gesto && (
            <div className="mt-6 text-left">
              <p className="rotulo">Seu desafio de hoje</p>
              <div className="santinho mt-2 p-4">
                <p className="leitura" style={{ fontSize: 17 }}>
                  {oracao.gesto}
                </p>
              </div>
            </div>
          )}

          {oracao.catecismo && (
            <div className="mt-6 text-left">
              <p className="rubrica">Para aprofundar</p>
              <p className="mt-1.5 text-[14px] leading-relaxed text-[color:var(--texto-2)]">
                {oracao.catecismo}
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={aoCompartilhar}
              className="botao-contorno aperta flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-[15px]"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-share-forward-line" aria-hidden />
              </span>
              Enviar para alguém
            </button>

            {mostrarAtivarAvisos && (
              <button
                type="button"
                onClick={aoAtivarAvisos}
                disabled={ativandoAvisos}
                className="botao-contorno aperta flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-[15px] disabled:opacity-60"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className={ativandoAvisos ? 'ri-loader-4-line animate-spin' : 'ri-notification-3-line'} aria-hidden />
                </span>
                Receber a oração às 6h
              </button>
            )}

            {!emAppInstalado() && (
              <button
                type="button"
                onClick={aoInstalar}
                className="botao-contorno aperta flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-[15px]"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-download-2-line" aria-hidden />
                </span>
                Instalar o app
              </button>
            )}
          </div>

          <p className="mt-6 text-[14px] text-[color:var(--texto-2)]">{FIM_AMANHA}</p>
        </div>
      )}
    </div>
  );
}